import { Component, ErrorHandler, Inject, ChangeDetectionStrategy, signal } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'

import { MatSnackBar } from '@angular/material/snack-bar'

import { SopimuksenPdfTemplateLatausPyynto, SopimuksenPdfTemplateLatausVastaus, Sopimus, SopimusLemonatorTyojono } from '@lemon/core-firebase/model/sopimus'
import { KirjautunutKayttajaService } from '../../_angular/service/kirjautunut-kayttaja.service'
import { TimestampService } from '../../_jaettu-angular/service/timestamp-service'
import { YllapitoUriService } from '../../_jaettu-lemonator/service/yllapito-uri.service'

import { TuettuKieli } from '@lemon/core/model/common'
import { firstValueFrom } from 'rxjs'

export interface SopimuksenJulkaiseminenDialogData {}

type TemplateStatus = 'idle' | 'uploading' | 'valid' | 'error'

@Component({
  templateUrl: './sopimuksen-julkaiseminen.dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SopimuksenJulkaiseminenDialog {
  // Luodaan avain valmiiksi, jotta templaatit voidaan ladata ennen julkaisua
  readonly versionAvain = this._firebase.firestoreCreateId()

  readonly fiTemplateStatus = signal<TemplateStatus>('idle')
  readonly enTemplateStatus = signal<TemplateStatus>('idle')
  readonly commonError = signal<string | null>(null)

  readonly julkaiseEnabled = signal(false)

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: SopimuksenJulkaiseminenDialogData,
    private dialogRef: MatDialogRef<SopimuksenJulkaiseminenDialog>,
    private _ladataanService: LadataanService,
    private _firebase: FirebaseLemonator,
    private _timestampService: TimestampService,
    private _snackbar: MatSnackBar,
    private _errorHandler: ErrorHandler,
    private _uriService: YllapitoUriService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
  ) {}

  peruta() {
    this.dialogRef.close()
  }

  async onTemplateFileChange(kieli: TuettuKieli, event: Event) {
    const input = event.target as HTMLInputElement
    if (!input.files?.length) {
      return
    }
    await this._uploadTemplate(kieli, input.files[0])
    // Nollataan valinta, jotta saman tiedoston voi ladata uudelleen virheen jälkeen
    input.value = ''
  }

  private _setTemplateStatus(kieli: TuettuKieli, status: TemplateStatus) {
    if (kieli === 'fi') {
      this.fiTemplateStatus.set(status)
    } else if (kieli === 'en') {
      this.enTemplateStatus.set(status)
    }
  }

  private async _uploadTemplate(kieli: TuettuKieli, file: File) {
    try {
      this.commonError.set(null)
      this._setTemplateStatus(kieli, 'uploading')

      const arrayBuffer = await file.arrayBuffer()
      const uint8Array = new Uint8Array(arrayBuffer)
      let binary = ''
      for (let i = 0; i < uint8Array.byteLength; i++) {
        binary += String.fromCharCode(uint8Array[i])
      }
      const pdfBase64 = window.btoa(binary)

      const pyynto: SopimuksenPdfTemplateLatausPyynto = {
        sopimusAvain: this.versionAvain,
        kieli,
        pdfBase64,
      }

      const vastaus = await this._firebase.functionsCall<SopimuksenPdfTemplateLatausPyynto, SopimuksenPdfTemplateLatausVastaus>('lataaUusiSopimuksenPdfTemplate', pyynto)

      if (vastaus?.e) {
        this._setTemplateStatus(kieli, 'error')
        if (vastaus.e === 'missing-fields') {
          this.commonError.set(`PDF-templaatista puuttuu kenttiä: ${vastaus.missingFields?.join(', ')}`)
          return
        }
        throw new Error(`${kieli} PDF-template:in lataus epäonnistui: ${vastaus.e}`)
      }

      this._setTemplateStatus(kieli, 'valid')
      this.julkaiseEnabled.set(this.fiTemplateStatus() === 'valid' && this.enTemplateStatus() === 'valid')
    } catch (err) {
      this._setTemplateStatus(kieli, 'error')
      this.julkaiseEnabled.set(false)
      this._errorHandler.handleError(err)
    }
  }

  async julkaise() {
    if (!this.julkaiseEnabled()) {
      return
    }

    try {
      this._ladataanService.aloitaLataaminen()

      const kirjanpitajanTiedot = await firstValueFrom(this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable)

      const batch = this._firebase.firestoreBatch()

      const sopimus: Sopimus = {
        avain: this.versionAvain,
        pvm: this._timestampService.now(),
        uid: kirjanpitajanTiedot.uid,
      }
      batch.set(this._uriService.annaSopimuksetMainUri(), sopimus, { merge: true })

      const sopimusVersio: Sopimus = {
        avain: this.versionAvain,
        pvm: this._timestampService.now(),
        uid: kirjanpitajanTiedot.uid,
      }
      batch.set(this._uriService.annaSopimuksetVersionUri(this.versionAvain), sopimusVersio)

      const tyojonoAvain = this._firebase.firestoreCreateId()
      const tyojono: SopimusLemonatorTyojono = {
        pvm: this._timestampService.now(),
      }
      batch.set(this._uriService.annaSopimuksetTyojononUri(tyojonoAvain), tyojono)

      await batch.commit()
      this._snackbar.open('Julkaiseminen onnistui', 'OK', { duration: 5000 })
      this.dialogRef.close()
    } catch (err) {
      this._errorHandler.handleError(err)
      this._snackbar.open('Julkaiseminen ei onnistunut', 'OK', { duration: 5000 })
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }
}
